import type { timeSpentField_AtlassianConversationAssistantMessageActionRenderers$data } from './time-spent-field/__generated__/timeSpentField_AtlassianConversationAssistantMessageActionRenderers.graphql';
import type { timeTrackingField_AtlassianConversationAssistantMessageActionRenderers$data } from './time-tracking-field/__generated__/timeTrackingField_AtlassianConversationAssistantMessageActionRenderers.graphql';

export type TimeTrackingSettingsLike = Partial<
	NonNullable<timeTrackingField_AtlassianConversationAssistantMessageActionRenderers$data>['timeTrackingSettings']
>;

export type TimeSpentSettingsLike = Partial<
	NonNullable<timeSpentField_AtlassianConversationAssistantMessageActionRenderers$data>['timeTrackingSettings']
>;

function normalizeDefaultUnit(unit: string | null | undefined): 'm' | 'h' | 'd' | 'w' {
	const u = (unit || '').toString().trim().toUpperCase();
	switch (u) {
		case 'M':
		case 'MINUTE':
			return 'm';
		case 'H':
		case 'HOUR':
			return 'h';
		case 'D':
		case 'DAY':
			return 'd';
		case 'W':
		case 'WEEK':
			return 'w';
		default:
			return 'h';
	}
}

export function formatSecondsToHuman(seconds: number | null | undefined): string | null {
	if (!seconds || seconds <= 0) {
		return null;
	}
	const hrs = Math.floor(seconds / 3600);
	const mins = Math.floor((seconds % 3600) / 60);
	const parts: string[] = [];
	if (hrs > 0) {
		parts.push(`${hrs}h`);
	}
	if (mins > 0) {
		parts.push(`${mins}m`);
	}
	return parts.length ? parts.join(' ') : '0m';
}

export function parseSuggestionToSeconds(
	input: string,
	settings: TimeTrackingSettingsLike | TimeSpentSettingsLike,
): number | null {
	if (!input) {
		return null;
	}
	const trimmed = input.trim().toLowerCase();
	if (!trimmed) {
		return null;
	}

	const H = settings?.workingHoursPerDay || 8;
	const D = settings?.workingDaysPerWeek || 5;

	const unitMap: Record<string, number> = {
		m: 60,
		h: 3600,
		d: 3600 * H,
		w: 3600 * H * D,
	};

	let total = 0;
	const regex = /(\d+)\s*([wdhm])/g;
	let found = false;
	let match: RegExpExecArray | null;
	while ((match = regex.exec(trimmed))) {
		found = true;
		const qty = parseInt(match[1], 10);
		const unit = match[2];
		total += qty * (unitMap[unit] || 0);
	}
	if (found) {
		return total || null;
	}

	// Fallback: if no explicit unit was provided, use defaultUnit (accepts enum or short forms)
	const numeric = parseInt(trimmed, 10);
	if (!Number.isNaN(numeric) && numeric >= 0) {
		const def = normalizeDefaultUnit(settings?.defaultUnit || 'HOUR');
		return numeric * (unitMap[def] || unitMap.h);
	}

	return null;
}

export function formatSecondsWithSettings(
	seconds: number | null | undefined,
	settings: TimeTrackingSettingsLike | TimeSpentSettingsLike | null | undefined,
): string | null {
	if (seconds == null || seconds < 0) {
		return null;
	}

	const H = settings?.workingHoursPerDay || 8;
	const D = settings?.workingDaysPerWeek || 5;
	const secPerMin = 60;
	const secPerHour = 3600;
	const secPerDay = H * secPerHour;
	const secPerWeek = D * secPerDay;

	function formatPretty(totalSeconds: number): string {
		let remaining = totalSeconds;
		const w = Math.floor(remaining / secPerWeek);
		remaining -= w * secPerWeek;
		const d = Math.floor(remaining / secPerDay);
		remaining -= d * secPerDay;
		const h = Math.floor(remaining / secPerHour);
		remaining -= h * secPerHour;
		const m = Math.floor(remaining / secPerMin);
		const parts: string[] = [];
		if (w) {
			parts.push(`${w}w`);
		}
		if (d) {
			parts.push(`${d}d`);
		}
		if (h) {
			parts.push(`${h}h`);
		}
		if (m) {
			parts.push(`${m}m`);
		}
		return parts.length ? parts.join(' ') : '0m';
	}

	function formatHours(totalSeconds: number): string {
		const h = Math.floor(totalSeconds / secPerHour);
		const m = Math.round((totalSeconds % secPerHour) / secPerMin);
		if (m === 0) {
			return `${h}h`;
		}
		return `${h}h ${m}m`;
	}

	function formatDays(totalSeconds: number): string {
		const d = Math.floor(totalSeconds / secPerDay);
		const remainder = totalSeconds % secPerDay;
		const h = Math.floor(remainder / secPerHour);
		const m = Math.round((remainder % secPerHour) / secPerMin);
		const parts: string[] = [];
		if (d) {
			parts.push(`${d}d`);
		}
		if (h) {
			parts.push(`${h}h`);
		}
		if (m) {
			parts.push(`${m}m`);
		}
		return parts.length ? parts.join(' ') : '0m';
	}

	switch (settings?.defaultFormat) {
		case 'HOURS':
			return formatHours(seconds);
		case 'DAYS':
			return formatDays(seconds);
		case 'PRETTY':
		default:
			return formatPretty(seconds);
	}
}
